import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { ShieldCheck, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const strip = (t) => (t || "").replace(/\*\*/g, "");

export const ConsentGate = () => {
  const { user, refreshUser } = useAuth();
  const [docs, setDocs] = useState(null);
  const [checked, setChecked] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const pending = (user && user.pending_legal) || [];
  const needed = !!user && pending.length > 0;

  useEffect(() => {
    if (needed && !docs) api.get("/legal/docs").then((r) => setDocs(r.data)).catch(() => {});
  }, [needed, docs]);

  if (!needed) return null;

  const order = docs ? docs.required : ["terms", "privacy", "training", "beta"];
  const allChecked = order.every((k) => checked[k]);

  const accept = async () => {
    setSubmitting(true);
    try {
      await api.post("/legal/accept", { docs: order });
      await refreshUser();
      toast.success("Thank you — you're cleared to begin.");
    } catch (e) {
      toast.error("Could not record your acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      data-testid="consent-gate">
      <div className="w-full max-w-lg my-6 rounded-sm border border-primary/30 bg-card p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-sm bg-primary/15 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <div>
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary">Before You Begin</span>
            <h2 className="font-display text-xl font-bold leading-tight">Review &amp; acknowledge</h2>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Please review and accept the following to start your training. You'll only be asked again if a document changes.
        </p>

        {!docs ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-3">
            {order.map((k) => {
              const d = docs.docs[k];
              const linkable = k === "terms" || k === "privacy";
              return (
                <label key={k} htmlFor={`consent-${k}`}
                  className={`flex gap-3 p-3.5 rounded-sm border cursor-pointer transition-colors ${
                    checked[k] ? "border-primary/50 bg-primary/5" : "border-border/70 bg-secondary/20 hover:border-border"}`}
                  data-testid={`consent-item-${k}`}>
                  <Checkbox id={`consent-${k}`} checked={!!checked[k]} data-testid={`consent-check-${k}`}
                    onCheckedChange={(v) => setChecked((c) => ({ ...c, [k]: !!v }))} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{d.title}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">v{d.version}</span>
                      {linkable && (
                        <Link to={`/legal/${k}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                          onClick={(e) => e.stopPropagation()} data-testid={`consent-link-${k}`}>
                          Read full <ExternalLink className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-3">{strip(d.content)}</p>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <Button onClick={accept} disabled={!allChecked || submitting} data-testid="consent-accept"
          className="w-full h-11 mt-5 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-semibold">
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          I agree and want to begin
        </Button>
      </div>
    </div>
  );
};
